import type { TooltipModel } from 'chart.js';

import cls from './custom-tooltip.module.css';

export interface TooltipItem {
    color?: string;
    label: string;
    value: string | number;
}

export interface CustomTooltipConfig {
    /**
     * Whether to use dark theme
     */
    isDark: boolean;
    /**
     * Optional title for the tooltip
     */
    title?: string;
    /**
     * Function to format tooltip items from Chart.js context
     */
    formatItems: (
        tooltipModel: TooltipModel<'bar' | 'line' | 'doughnut'>,
    ) => TooltipItem[];
    /**
     * Optional function to determine badge variant based on value
     */
    getBadgeVariant?: (
        value: number,
    ) => 'positive' | 'negative' | 'neutral' | null;
    /**
     * Whether to hide the color indicator square
     */
    shouldHideColorIndicator?: boolean;
}

let tooltipEl: HTMLDivElement | null = null;

/**
 * Creates or updates a premium custom HTML tooltip for Chart.js
 *
 * This creates a modern, glassmorphic tooltip with:
 * - Backdrop blur effect
 * - Smooth animations
 * - Premium gradients and shadows
 * - Adaptive theming (dark/light)
 * - Color indicators for dataset items
 * - Optional badge styling for values
 *
 * @param context - Chart.js tooltip context
 * @param config - Configuration for tooltip appearance and formatting
 */
export const createCustomTooltip =
    (config: CustomTooltipConfig) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (context: any) => {
        const { chart, tooltip } = context;
        const {
            isDark,
            title,
            formatItems,
            getBadgeVariant,
            shouldHideColorIndicator,
        } = config;

        // Create tooltip element on first render
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.className = cls.tooltip;
            document.body.appendChild(tooltipEl);
        }

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.classList.remove(cls.visible);

            return;
        }

        // Format tooltip items
        const items = formatItems(tooltip);

        // Build tooltip HTML
        const themeClass = isDark ? cls.dark : cls.light;
        const titleHtml = title
            ? `<div class="${cls.tooltipTitle} ${themeClass}">${title}</div>`
            : '';

        const itemsHtml = items
            .map((item) => {
                const colorIndicator =
                    item.color && !shouldHideColorIndicator
                        ? `<div class="${cls.colorIndicator}" style="background-color: ${item.color}"></div>`
                        : '';

                let valueHtml = '';

                // Check if we should use badge styling
                if (getBadgeVariant && typeof item.value === 'number') {
                    const variant = getBadgeVariant(item.value);

                    if (variant) {
                        valueHtml = `<span class="${cls.tooltipBadge} ${cls[variant]}">${item.value}</span>`;
                    } else {
                        valueHtml = `<span class="${cls.tooltipValue} ${themeClass}">${item.value}</span>`;
                    }
                } else {
                    valueHtml = `<span class="${cls.tooltipValue} ${themeClass}">${item.value}</span>`;
                }

                return `
                    <div class="${cls.tooltipItem}">
                        <div class="${cls.tooltipLabel} ${themeClass}">
                            ${colorIndicator}
                            <span>${item.label}</span>
                        </div>
                        ${valueHtml}
                    </div>
                `;
            })
            .join('');

        tooltipEl.innerHTML = `
            <div class="${cls.tooltipContent} ${themeClass}">
                ${titleHtml}
                <div class="${cls.tooltipBody}">
                    ${itemsHtml}
                </div>
                <div class="${cls.tooltipArrow} ${themeClass} ${cls.bottom}"></div>
            </div>
        `;

        // Position tooltip
        const position = chart.canvas.getBoundingClientRect();
        const tooltipWidth = tooltipEl.offsetWidth;
        const tooltipHeight = tooltipEl.offsetHeight;

        // Calculate position (centered above the point)
        let left =
            position.left + window.scrollX + tooltip.caretX - tooltipWidth / 2;
        let top =
            position.top + window.scrollY + tooltip.caretY - tooltipHeight - 12;

        // Ensure tooltip stays within viewport
        const padding = 10;

        if (left < padding) {
            left = padding;
        } else if (left + tooltipWidth > window.innerWidth - padding) {
            left = window.innerWidth - tooltipWidth - padding;
        }

        if (top < padding) {
            top = position.top + window.scrollY + tooltip.caretY + 12;
        }

        tooltipEl.style.left = `${left}px`;
        tooltipEl.style.top = `${top}px`;
        tooltipEl.classList.add(cls.visible);
    };

/**
 * Cleanup function to remove tooltip element from DOM
 * Call this when unmounting a component that uses custom tooltips
 */
export const cleanupCustomTooltip = () => {
    if (tooltipEl && tooltipEl.parentNode) {
        tooltipEl.parentNode.removeChild(tooltipEl);
        tooltipEl = null;
    }
};
